import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators'

import { DEFAULT_SERVER_ERROR } from 'utils/ErrorMessages'

import { addToast } from 'containers/Toasts/actions'

import {
  getDeepfakeConfigError,
  getDeepfakeConfigSuccess,
  updateDeepfakeConfigError,
  updateDeepfakeConfigSuccess,
} from './actions'
import {
  ADD_WARNING_REPORT_RECIPIENTS_ASYNC,
  DELETE_WARNING_REPORT_RECIPIENTS_ASYNC,
  GET_DEEPFAKE_CONFIG_ASYNC,
  GET_DEEPFAKE_WIZARD_CONFIG_ASYNC,
  GET_WARNING_REPORT_CANDIDATES_ASYNC,
  GET_WARNING_REPORT_RECIPIENTS_ASYNC,
  UPDATE_DEEPFAKE_CONFIG_ASYNC,
  UPDATE_DEEPFAKE_WIZARD_CONFIG_ASYNC,
  VERIFY_CONSENT_ASYNC,
} from './constants'
import {
  selectConsentData,
  selectConsentLink,
  selectConsentText,
  selectConsentTimeoutAmountInSeconds,
  selectConsentType,
  selectEmailAlertsForInternalsInExternalMeetings,
  selectJoinExternalMeetings,
  selectLiveDeepfakeAlert,
  selectMfaEnabled,
  selectMfaFailureAlertEnabled,
  selectMfaGlobalTimerSeconds,
  selectMfaLinkTimeoutSeconds,
  selectMfaSuspicionAlertEnabled,
  selectPopupAudience,
  selectSendWarningReportNotification,
  selectSiemEventsEnabled,
} from './selectors'

const DEFAULT_SUCCESS_MESSAGE = 'Warning report was successfully updated!'

const getDeepFakeConfigEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_DEEPFAKE_CONFIG_ASYNC.INITIATE),
    switchMap(() =>
      api.settings.getDeepfakeConfig().pipe(
        map((response) => getDeepfakeConfigSuccess(response)),
        catchError((error) => {
          const actions = [epicsErrorHandler(getDeepfakeConfigError, error)]
          actions.push(
            addToast({
              message: 'Failed to load Deepfake configuration. Please try again.',
              type: 'error',
            })
          )
          return of(...actions)
        }),
        takeUntil(action$.pipe(ofType(GET_DEEPFAKE_CONFIG_ASYNC.CANCEL)))
      )
    )
  )

const updateDeepFakeConfigEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_DEEPFAKE_CONFIG_ASYNC.INITIATE),
    switchMap((action) => {
      const state = state$.value
      const consentType = selectConsentType(state)
      const consentData = selectConsentData(state)

      const body = {
        companySettings: {
          popupAudience: selectPopupAudience(state),
          sendWarningReportNotification: selectSendWarningReportNotification(state),
          liveDeepfakeAlert: selectLiveDeepfakeAlert(state),
          endCall: false,
          consentText: selectConsentText(state),
          consentLink: selectConsentLink(state) || null,
          consentTimeoutAmountInSeconds: selectConsentTimeoutAmountInSeconds(state) || null,
          joinExternalMeetings: selectJoinExternalMeetings(state),
          emailAlertsForInternalsInExternalMeetings:
            selectEmailAlertsForInternalsInExternalMeetings(state),
          consentType,
          consentData,
          mfaEnabled: selectMfaEnabled(state),
          mfaLinkTimeoutSeconds: selectMfaLinkTimeoutSeconds(state),
          mfaGlobalTimerSeconds: selectMfaGlobalTimerSeconds(state),
          mfaSuspicionAlertEnabled: selectMfaSuspicionAlertEnabled(state),
          mfaFailureAlertEnabled: selectMfaFailureAlertEnabled(state),
          siemEventsEnabled: selectSiemEventsEnabled(state),
        },
      }

      // Handle consentSettings update (multiple fields at once)
      if (action.payload.key === 'consentSettings') {
        body.companySettings.consentText = action.payload.value.consentText
        body.companySettings.consentLink = action.payload.value.consentLink || null
      } else if (action.payload.key === 'consentDataSettings') {
        // Update consentData[consentType] instead of top-level fields
        const { consentType: updatedConsentType, text, link } = action.payload.value
        const currentConsentData = consentData || {}
        // Preserve existing timeout if it exists
        const existingTimeout = currentConsentData[updatedConsentType]?.timeout || null
        body.companySettings.consentData = {
          ...currentConsentData,
          [updatedConsentType]: {
            text,
            link: link || null,
            timeout: existingTimeout,
          },
        }
      } else {
        // Handle individual field updates
        const consentFields = [
          'consentText',
          'consentLink',
          'consentTimeoutAmountInSeconds',
          'consentType',
        ]
        if (consentFields.includes(action.payload.key)) {
          body.companySettings[action.payload.key] = action.payload.value
        }
      }

      return api.settings.updateDeepfakeConfig(body).pipe(
        mergeMap(() => {
          const actions = [updateDeepfakeConfigSuccess(action.payload.key)]

          // Add success toast for consent settings
          if (
            action.payload.key === 'consentSettings' ||
            action.payload.key === 'consentDataSettings'
          ) {
            actions.push(
              addToast({
                message: 'Consent message was updated successfully',
                type: 'success',
              })
            )
          }

          return of(...actions)
        }),
        catchError((error) => {
          const actions = [epicsErrorHandler(updateDeepfakeConfigError, error)]

          const errorMessage = 'Failed to update settings. Please try again.'

          actions.push(
            addToast({
              message: errorMessage,
              type: 'error',
            })
          )

          return of(...actions)
        }),
        takeUntil(action$.pipe(ofType(UPDATE_DEEPFAKE_CONFIG_ASYNC.CANCEL)))
      )
    })
  )

const getWarningReportRecipientsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_WARNING_REPORT_RECIPIENTS_ASYNC.INITIATE),
    switchMap(() =>
      api.settings.getWarningReportRecipients().pipe(
        map((response) => GET_WARNING_REPORT_RECIPIENTS_ASYNC.onSuccess({ data: response })),
        catchError((error) =>
          of(epicsErrorHandler(GET_WARNING_REPORT_RECIPIENTS_ASYNC.onError, error))
        ),
        takeUntil(action$.pipe(ofType(GET_WARNING_REPORT_RECIPIENTS_ASYNC.CANCEL)))
      )
    )
  )

const getWarningReportCandidatesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_WARNING_REPORT_CANDIDATES_ASYNC.INITIATE),
    switchMap(() =>
      api.settings.getWarningReportCandidates().pipe(
        map((response) => GET_WARNING_REPORT_CANDIDATES_ASYNC.onSuccess({ data: response })),
        catchError((error) =>
          of(epicsErrorHandler(GET_WARNING_REPORT_CANDIDATES_ASYNC.onError, error))
        ),
        takeUntil(action$.pipe(ofType(GET_WARNING_REPORT_CANDIDATES_ASYNC.CANCEL)))
      )
    )
  )

const addWarningReportRecipientsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(ADD_WARNING_REPORT_RECIPIENTS_ASYNC.INITIATE),
    switchMap((action) =>
      api.settings
        .addWarningReportRecipients(action.payload.idsToCreate.map((id) => ({ userId: id })))
        .pipe(
          mergeMap((response) =>
            of(
              ADD_WARNING_REPORT_RECIPIENTS_ASYNC.onSuccess(response),
              addToast({ message: DEFAULT_SUCCESS_MESSAGE })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(ADD_WARNING_REPORT_RECIPIENTS_ASYNC.onError, error),
              addToast({
                message: DEFAULT_SERVER_ERROR,
                type: 'error',
              })
            )
          ),
          takeUntil(action$.pipe(ofType(ADD_WARNING_REPORT_RECIPIENTS_ASYNC.CANCEL)))
        )
    )
  )

const deleteWarningReportRecipientsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DELETE_WARNING_REPORT_RECIPIENTS_ASYNC.INITIATE),
    switchMap((action) =>
      api.settings
        .deleteWarningReportRecipients({
          ids: action.payload.idsToDelete,
        })
        .pipe(
          mergeMap((response) =>
            of(
              DELETE_WARNING_REPORT_RECIPIENTS_ASYNC.onSuccess(response),
              addToast({ message: DEFAULT_SUCCESS_MESSAGE })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(DELETE_WARNING_REPORT_RECIPIENTS_ASYNC.onError, error),
              addToast({
                message: DEFAULT_SERVER_ERROR,
                type: 'error',
              })
            )
          ),
          takeUntil(action$.pipe(ofType(DELETE_WARNING_REPORT_RECIPIENTS_ASYNC.CANCEL)))
        )
    )
  )

const getDeepfakeWizardConfigEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_DEEPFAKE_WIZARD_CONFIG_ASYNC.INITIATE),
    mergeMap(() =>
      api.settings.getDeepfakeWizardConfig().pipe(
        map((response) => GET_DEEPFAKE_WIZARD_CONFIG_ASYNC.onSuccess({ data: response })),
        catchError((error) =>
          of(epicsErrorHandler(GET_DEEPFAKE_WIZARD_CONFIG_ASYNC.onError, error))
        ),
        takeUntil(action$.pipe(ofType(GET_DEEPFAKE_WIZARD_CONFIG_ASYNC.CANCEL)))
      )
    )
  )

const updateDeepfakeWizardConfigEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_DEEPFAKE_WIZARD_CONFIG_ASYNC.INITIATE),
    mergeMap(() =>
      api.settings.updateDeepfakeWizardConfig({ is_eula_signed: true }).pipe(
        map((response) => UPDATE_DEEPFAKE_WIZARD_CONFIG_ASYNC.onSuccess({ data: response })),
        catchError((error) =>
          of(epicsErrorHandler(UPDATE_DEEPFAKE_WIZARD_CONFIG_ASYNC.onError, error))
        ),
        takeUntil(action$.pipe(ofType(UPDATE_DEEPFAKE_WIZARD_CONFIG_ASYNC.CANCEL)))
      )
    )
  )

const verifyConsentEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(VERIFY_CONSENT_ASYNC.INITIATE),
    mergeMap(() =>
      api.settings.verifyConsent().pipe(
        map(({ response }) => VERIFY_CONSENT_ASYNC.onSuccess({ data: response })),
        catchError((error) => of(epicsErrorHandler(VERIFY_CONSENT_ASYNC.onError, error))),
        takeUntil(action$.pipe(ofType(VERIFY_CONSENT_ASYNC.CANCEL)))
      )
    )
  )

export default combineEpics(
  getDeepFakeConfigEpic,
  updateDeepFakeConfigEpic,
  getWarningReportRecipientsEpic,
  getWarningReportCandidatesEpic,
  addWarningReportRecipientsEpic,
  deleteWarningReportRecipientsEpic,
  getDeepfakeWizardConfigEpic,
  updateDeepfakeWizardConfigEpic,
  verifyConsentEpic
)
